import { memo, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement,
  PointElement, LineElement, Title, Tooltip, Legend,
} from 'chart.js';
import { Bar, Pie, Line } from 'react-chartjs-2';
import {
  buildWorkTypeMonthlyData, buildDeptOpenClosed, buildMachinePie, buildMachineLine,
  buildMonthlyTrend, buildProductComparison, buildRejectionTrend, barOptions, lineOptions,
  productBarChartOptions, productLineChartOptions,
  hBarOptions, comboOptions,
  buildProductSupplyBar, buildProductWarrantyBar, buildProductRejectionLine, buildTop5RejectionBar,
  buildSupplyGrowthCombo, buildWarrantyGrowthCombo, buildRejectionRateByProductPeriod,
  rejectionPeriodChartOptions,
  buildWorkTypePie, buildDeptWorkTypeStacked, buildDailyTrend, buildCompletedOpenByWorkType,
  buildOpenByDeptDonut, buildMachineWiseTotalCompleted, buildMachineMonthlyCluster,
  machineMonthlyClusterChartOptions, machineTotalCompletedChartOptions, dailyTrendChartOptions,
  deptWorkTypeStackedChartOptions, completedOpenWorkTypeChartOptions,
} from '../../utils/dashboardUtils';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, PointElement, LineElement, Title, Tooltip, Legend);

const NoData = () => (
  <Box display="flex" alignItems="center" justifyContent="center" height="100%" color="text.secondary">
    <Typography variant="body2">No data for selected period</Typography>
  </Box>
);

export function WorkTypeMonthlyChart({ records, year }) {
  if (!records?.length) return <NoData />;
  return <Bar data={buildWorkTypeMonthlyData(records, year)} options={barOptions} />;
}

export function DeptOpenClosedChart({ records }) {
  if (!records?.length) return <NoData />;
  const data = buildDeptOpenClosed(records);
  if (!data.labels.length) return <NoData />;
  return <Bar data={data} options={barOptions} />;
}

export function MachinePieChart({ records }) {
  if (!records?.length) return <NoData />;
  const data = buildMachinePie(records);
  if (!data.labels.length) return <NoData />;
  return <Pie data={data} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'right' } } }} />;
}

export function MachineLineChart({ records, year }) {
  if (!records?.length) return <NoData />;
  const data = buildMachineLine(records, year);
  if (!data.datasets.length) return <NoData />;
  return <Line data={data} options={lineOptions} />;
}

export function MonthlyTrendChart({ records, label, color }) {
  if (!records?.length) return <NoData />;
  const trend = buildMonthlyTrend(records);
  if (!trend.labels.length) return <NoData />;
  return (
    <Line data={{
      labels: trend.labels,
      datasets: [{ label, data: trend.data, borderColor: color, backgroundColor: color, tension: 0.3, fill: false }],
    }} options={lineOptions} />
  );
}

export function ProductComparisonChart({ supply, warranty }) {
  if (!supply?.length && !warranty?.length) return <NoData />;
  return <Bar data={buildProductComparison(supply || [], warranty || [])} options={barOptions} />;
}

export function RejectionTrendChart({ records }) {
  if (!records?.length) return <NoData />;
  const trend = buildRejectionTrend(records);
  if (!trend.labels.length) return <NoData />;
  return (
    <Line data={{
      labels: trend.labels,
      datasets: [{ label: 'Avg Warranty %', data: trend.data, borderColor: '#db4f4a', tension: 0.3, fill: false }],
    }} options={lineOptions} />
  );
}

const ChartContainer = ({ children }) => (
  <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>{children}</Box>
);

export function ProductSupplyBarChart({ records, product }) {
  if (!records?.length) return <NoData />;
  const data = buildProductSupplyBar(records, product);
  return (
    <ChartContainer>
      <Bar data={data} options={productBarChartOptions} redraw={false} />
    </ChartContainer>
  );
}

export function ProductWarrantyBarChart({ records, product }) {
  if (!records?.length) return <NoData />;
  const data = buildProductWarrantyBar(records, product);
  return (
    <ChartContainer>
      <Bar data={data} options={productBarChartOptions} redraw={false} />
    </ChartContainer>
  );
}

export function ProductRejectionLineChart({ supply, warranty, product }) {
  if (!supply?.length && !warranty?.length) return <NoData />;
  return (
    <ChartContainer>
      <Line data={buildProductRejectionLine(supply || [], warranty || [], product)} options={productLineChartOptions} redraw={false} />
    </ChartContainer>
  );
}

export function Top5RejectionBarChart({ supply, warranty, product }) {
  if (!supply?.length && !warranty?.length) return <NoData />;
  return <Bar data={buildTop5RejectionBar(supply || [], warranty || [], product)} options={hBarOptions} />;
}

export function SupplyGrowthComboChart({ records, product }) {
  if (!records?.length) return <NoData />;
  const data = buildSupplyGrowthCombo(records, product);
  if (!data.labels.length) return <NoData />;
  return <Bar data={data} options={comboOptions} />;
}

export function WarrantyGrowthComboChart({ supply, warranty, product }) {
  if (!warranty?.length) return <NoData />;
  const data = buildWarrantyGrowthCombo(supply || [], warranty, product);
  if (!data.labels.length) return <NoData />;
  return <Bar data={data} options={comboOptions} />;
}

export const RejectionByProductPeriodChart = memo(function RejectionByProductPeriodChart({ records, product }) {
  const data = useMemo(() => buildRejectionRateByProductPeriod(records || [], product), [records, product]);
  if (!records?.length || !data.labels.length) return <NoData />;
  return (
    <Box sx={{ position: 'relative', height: '100%', minHeight: 180, width: '100%' }}>
      <Bar data={data} options={rejectionPeriodChartOptions} redraw={false} />
    </Box>
  );
});

export function WorkTypePieChart({ records }) {
  if (!records?.length) return <NoData />;
  const data = buildWorkTypePie(records);
  if (!data.labels.length) return <NoData />;
  return <Pie data={data} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'right' } } }} />;
}

export function DeptWorkTypeStackedChart({ records }) {
  if (!records?.length) return <NoData />;
  const data = buildDeptWorkTypeStacked(records);
  if (!data.labels.length) return <NoData />;
  return (
    <ChartContainer>
      <Bar data={data} options={deptWorkTypeStackedChartOptions} redraw={false} />
    </ChartContainer>
  );
}

export function DailyTrendChart({ records, year, month }) {
  if (!records?.length) return <NoData />;
  const data = buildDailyTrend(records, year, month);
  if (!data.labels.length) return <NoData />;
  return (
    <ChartContainer>
      <Line data={data} options={dailyTrendChartOptions} redraw={false} />
    </ChartContainer>
  );
}

export function CompletedOpenWorkTypeChart({ records }) {
  if (!records?.length) return <NoData />;
  const data = buildCompletedOpenByWorkType(records);
  if (!data.labels.length) return <NoData />;
  return (
    <ChartContainer>
      <Bar data={data} options={completedOpenWorkTypeChartOptions} redraw={false} />
    </ChartContainer>
  );
}

export function OpenByDeptDonutChart({ records }) {
  if (!records?.length) return <NoData />;
  const data = buildOpenByDeptDonut(records);
  if (!data.labels.length) return <NoData />;
  return <Pie data={data} options={{ responsive: true, maintainAspectRatio: false, cutout: '55%', plugins: { legend: { position: 'left' } } }} />;
}

export function MachineTotalCompletedChart({ records }) {
  if (!records?.length) return <NoData />;
  const data = buildMachineWiseTotalCompleted(records);
  if (!data.labels.length) return <NoData />;
  return <Bar data={data} options={machineTotalCompletedChartOptions} />;
}

export function MachineMonthlyClusterChart({ records }) {
  const data = useMemo(() => buildMachineMonthlyCluster(records || []), [records]);
  if (!records?.length || !data.datasets.length || !data.labels.length) return <NoData />;
  return <Bar data={data} options={machineMonthlyClusterChartOptions} redraw={false} />;
}
